import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import ConfirmDialog from "../../components/ConfirmDialog";
import SupplyModal from "./SupplyModal";
import SupplyReport from "./SupplyReport";
import ReturnSupplyModal from "./ReturnSupplyModal";
import { canEditSupply, getSupplyRowSx } from "./supplyHelpers";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useDateRange, periodRequestConfig } from "../../hooks/useDateRange";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip, useTheme } from '@mui/material';
import { DATE_FILTER_OPTIONS, LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AddCircleOutline, AssignmentReturn, CancelOutlined, CheckCircleOutline, Delete, DeleteOutline, Edit, Refresh } from "@mui/icons-material";
import TableView from "../../components/TableView";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { useAppSocket } from "../../providers/AppSocketContext";
import { createSignCell } from "../../util/cellFormatters";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { canAmend, canDrop, canRecord } from '../../util/access';
const SupplyRecord = () => {
	const [toEdit, setToEdit] = useState<any>(null);
	const [toReturn, setToReturn] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [newRecord, setNewRecord] = useState<boolean>(false);
	const [toView, setToView] = useState<any>(null);

	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } = useData(LocalStorageKeys.SUPPLY_BATCHED_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.SUPPLY_BATCHED_RECORDS);
	const { appSettings } = useAppSocket();
	const theme = useTheme();

	const canReturn = canRecord(access, 'supply_return');
	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		if (!loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period]);

	const fetchData = async (useLoadModal: boolean = true, forceUpdate: boolean = false) => {
		setLoading(true);

		const res = await api.get(`supplies`, {
			...(periodRequestConfig(period))
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, period, forceUpdate);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handleDelete = async () => {
		if (toDelete) {
			setLoadingMessage("DELETE");

			const res = await api.delete(`/supplies/${toDelete.batch}`);

			if (res?.status === 204) {
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
				fetchData(false, true);
			} else if (res?.status === 409) {
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(handleDelete);
			}

			setLoadingMessage(null);
			setToDelete(null);
		}
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length > 0) {
			const payload = { batches: toDeleteBulk };

			setLoadingMessage("DELETE");

			const res = await api.post("/supplies/delete", payload);

			if (res?.status === 204) {
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
				tableRef.current?.resetRowSelection();
				fetchData(false, true);
			} else if (res?.status === 409) {
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(handleBulkDelete);
			}

			setLoadingMessage(null);
			setToDeleteBulk([]);
		}
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"),
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.type"),
			accessorKey: "sup_type",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.reference"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.observer"),
			accessorKey: "client",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorFn: (row: any) => Number(row.amount),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.supplier"),
			accessorKey: "supplier",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.depot"),
			accessorKey: "depot",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.inStock"), accessorKey: "is_stocked",
			Cell: ({ cell }: { cell: any }) => (
				cell.getValue() ?
					<CheckCircleOutline sx={{ color: "green" }} /> :
					<CancelOutlined sx={{ color: "red" }} />
			)
		},
		{
			header: t("common:supplyDetail.dueDate"),
			id: "due_date",
			accessorFn: (row: any) => (row.due_date ? new Date(row.due_date) : null),
			Cell: ({ row }: { row: any }) =>
				row.original.due_date
					? Util.formatDate(row.original.due_date, "medium")
					: "—",
			filterVariant: 'date-range',
		},
	], [t]);

	const handleChanges = () => {
		toastIdRef.current = toast.loading(`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`);
		fetchData(false, true);
	}

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={LocalStorageKeys.SUPPLY_BATCHED_RECORDS}
				posting="supply"
					dataScope="supply"
					header={t("common:supply.headers.records")}
					data={data}
					columns={columns}
					totals={[{ key: "amount", label: t("common:tables.amount") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					getRowSx={(row) => getSupplyRowSx(row.original, theme)}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.newSupply")}>
								<IconButton
									onClick={() => {
										if (allowOrWarn(canRecord(access, 'supply'))) {
											setNewRecord(true);
										}
									}}
									color="success">
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							{[
								...new Set(
									table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch),
								),
							].length === 1
								&& canEditSupply(table.getSelectedRowModel().flatRows[0].original) && (
								<Tooltip title={t("common:ui.tooltips.editSelectedSupply")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canAmend(access, 'supply'))) {
												setToEdit(table.getSelectedRowModel().flatRows[0].original);
											}
										}}
										color="primary"
									>
										<Edit />
									</IconButton>
								</Tooltip>
							)}
							{[
								...new Set(
									table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch),
								),
							].length === 1
								&& Util.compareStrings(
									table.getSelectedRowModel().flatRows[0].original.sup_type || "Supply",
									"Supply",
								)
								&& canReturn && (
								<Tooltip title={t("common:ui.tooltips.returnSelectedSupply")}>
									<IconButton
										onClick={() => setToReturn(table.getSelectedRowModel().flatRows[0].original)}
										color="warning"
									>
										<AssignmentReturn />
									</IconButton>
								</Tooltip>
							)}
							{table.getSelectedRowModel().flatRows.length > 0 && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'supply', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;

													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error">
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="supply"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)
					}
					renderRowActionMenuItems={(row, closeMenu) => [
						!(toDelete || toEdit || toReturn) && canEditSupply(row.original) ? (
							<MenuItem
								key={0}
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'supply'))) {
										setToEdit(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn)
							&& Util.compareStrings(row.original.sup_type || "Supply", "Supply")
							&& canReturn ? (
							<MenuItem
								key="return"
								onClick={() => {
									setToReturn(row.original);
									closeMenu();
								}}
								sx={{ color: "warning.main" }}
							>
								<ListItemIcon sx={{ color: "warning.main" }}>
									<AssignmentReturn />
								</ListItemIcon>
								{t("common:ui.buttons.return")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) ? (
							<MenuItem
								key={1}
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'supply'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>
			</LocalizationProvider >

			{/* {/* New | Edit Modal */
				< SupplyModal
					isOpen={newRecord || !!toEdit}
					toggle={() => { setNewRecord(false); setToEdit(null); }}
					bundle={toEdit}
					onSuccess={handleChanges}
				/>
			}

			{/* Report Modal */
				toView &&
				<SupplyReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			}

			{toReturn && (
				<ReturnSupplyModal
					isOpen={!!toReturn}
					toggle={() => setToReturn(null)}
					bundle={toReturn}
					onSuccess={handleChanges}
				/>
			)}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={toDelete ? handleDelete : handleBulkDelete}
					onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]) }}
					isOpen={toDelete || toDeleteBulk.length > 0}
					setIsOpen={() => { }}
					title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
					message={
						toDelete ?
							<>
								{t("common:supply.confirm.deleteSingleStart")}
								 <b><u>{toDelete?.depot}</u></b>&nbsp;
								{t("common:supply.confirm.deleteSingleValue")}&nbsp;
								<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
								{t("common:supply.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>
								, {t("common:tables.reference").toLowerCase()} <button
									style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
									onClick={() => setToView(toDelete)}
								>
									<b><u>{toDelete?.reference}</u></b>
								</button>.
							</>
							:
							<>
								{t("common:supply.confirm.deleteBulk", { count: Number(toDeleteBulk.length) })}
							</>
					}
					confirmBtnColor="error"
					confirmBtnVariant="contained"
					confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
				/>
			}
		</>
	);
};

export default SupplyRecord;
